import { useQuery } from "@tanstack/react-query";
import { endOfDay, startOfDay } from "date-fns";
import { uniqBy } from "lodash";
import flatten from "lodash/flatten";
import { useGeotabClient } from "@/components/GeotabClientContext";

export const ANNOTATION_LIMIT = 1000;

interface Args {
  toDate: Date;
  fromDate: Date;
  deviceIds?: string[];
}

/**
 * Returns annotations for the given devices within the given date range.
 * @param args - Arguments for the query.
 * @returns - Annotations for the given devices within the given date range.
 */
export function useTripAnnotationForDevices({ toDate, fromDate, deviceIds = [] }: Args) {
  toDate = endOfDay(toDate);
  fromDate = startOfDay(fromDate);
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["annotations", activeSessionId, deviceIds, fromDate, toDate],

    queryFn: ({ signal }) => {
      return Promise.all(
        uniqBy(deviceIds, id => id).map(deviceId =>
          geotabClient.get(
            "TripAnnotation",
            {
              toDate,
              fromDate,
              deviceSearch: { id: deviceId },
            },
            ANNOTATION_LIMIT / 3,
            signal,
          ),
        ),
      )
        .then(annotations => flatten(annotations))
        .then(annotations => uniqBy(annotations, a => a.id));
    },

    enabled: !!deviceIds,
  });
}
